import type { Country, LabResult, Profile } from './localData';

// Groups readings of the same analyte for one person/country/laboratory into
// one trend series. A laboratory is part of the key because its printed
// reference range may differ even within one country.
export function labSeriesKey(analyte: string, country: Country, owner: Profile, canonicalCode?: string, unit?: string, labName?: string): string {
  const identity = (canonicalCode || analyte).trim();
  return `${encodeURIComponent(identity)}|${country}|${encodeURIComponent(owner)}|${encodeURIComponent(unit || '')}|${encodeURIComponent(labName || '')}`;
}

export function parseLabSeriesKey(key: string): { analyte: string; country: Country; owner: Profile; unit?: string; labName?: string } {
  const [analyteEnc, country, owner, unitEnc, labNameEnc] = key.split('|');
  return {
    analyte: decodeURIComponent(analyteEnc || ''),
    country: country === 'russia' || country === 'israel' ? country : 'other',
    owner: decodeURIComponent(owner || 'anton'),
    unit: unitEnc ? decodeURIComponent(unitEnc) : undefined,
    labName: labNameEnc ? decodeURIComponent(labNameEnc) : undefined,
  };
}

// Same filter MetricDetail uses to resolve a series key into its readings —
// shared so the comparison screen (multiple independent panels, one per
// selected key) shows exactly what the single-series page would show for
// that same key. Returns null for a missing key rather than an empty
// readings array, so callers can tell "no key" apart from "key parsed but
// nothing matched".
export function seriesReadingsForKey(labResults: LabResult[], key?: string): { parsed: ReturnType<typeof parseLabSeriesKey>; readings: LabResult[] } | null {
  if (!key) return null;
  const parsed = parseLabSeriesKey(key);
  // Four-part keys were shared before laboratory became part of a series.
  // Keep them backwards-compatible, while a new five-part key ending in an
  // empty segment deliberately means "only readings with no lab recorded".
  const hasLaboratoryScope = key.split('|').length >= 5;
  const readings = labResults
    .filter(r => (r.canonicalCode || r.analyte) === parsed.analyte && r.country === parsed.country && r.owner === parsed.owner && (!parsed.unit || (r.unit || '') === parsed.unit) && (!hasLaboratoryScope || r.labName === parsed.labName))
    .sort((a, b) => a.takenOn.localeCompare(b.takenOn));
  return { parsed, readings };
}

// null means "no printed reference range to judge against" — deliberately
// distinct from true/false so the UI can show a neutral "норма не указана"
// instead of a misleading in/out-of-range badge.
export function inRange(value: number | undefined, low?: number, high?: number): boolean | null {
  if (value === undefined || (low === undefined && high === undefined)) return null;
  if (low !== undefined && value < low) return false;
  if (high !== undefined && value > high) return false;
  return true;
}

// Decides whether a lab-item draft's typed/extracted value is numeric
// ("95") or qualitative text ("отрицательно") — see LabDraftItem in
// App.tsx, whose `value` field holds either.
export function isNumericLabValue(raw: string): boolean {
  return raw.trim() !== '' && Number.isFinite(Number(raw.trim()));
}
